/** @jsxImportSource react */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ticketsAPI, clientAPI, deviceAPI, userAPI } from '../services/api';
import HelpGuide from './HelpGuide';

interface Ticket {
  id: number;
  client_id: number;
  device_id?: number;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  category: 'general' | 'hardware' | 'software' | 'connectivity' | 'billing';
  created_by: string;
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  client_name: string;
  company_name: string;
  device_serial?: string;
  updates?: TicketUpdate[];
}

interface TicketUpdate {
  id: number;
  ticket_id: number;
  update_type: 'comment' | 'status_change' | 'assignment' | 'priority_change';
  content?: string;
  old_value?: string;
  new_value?: string;
  created_by: string;
  created_at: string;
}

const Tickets: React.FC<{ prefillTicket?: { unitId: string; name: string; platform: string; issue: string } | null }> = ({ prefillTicket }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [archivedTickets, setArchivedTickets] = useState<Ticket[]>([]);
  const [activeTab, setActiveTab] = useState<'active'|'archived'>('active');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketDetails, setTicketDetails] = useState<any>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ status: '', priority: '' });
  const [clients, setClients] = useState<any[]>([]);
  const [readComments, setReadComments] = useState<Set<string>>(new Set());
  const [respondedTickets, setRespondedTickets] = useState<Set<string>>(new Set());
  const [masterAdminUsers, setMasterAdminUsers] = useState<string[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [supportHours, setSupportHours] = useState<{ allocated: number; used: number; outside: number } | null>(null);

  // Check if current user is a client
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clientUser = JSON.parse(localStorage.getItem('clientUser') || '{}');
  const isClient = user.type === 'client' || !!clientUser.client_id;
  const currentClientId = user.client_id || clientUser.client_id || null;

  const [newTicket, setNewTicket] = useState({
    client_id: isClient ? currentClientId : '',
    device_id: '',
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });
  const [ticketStats, setTicketStats] = useState({
    openTickets: 0,
    avgResolutionTime: 0,
    longestOpenTicket: 0
  });
  const [newComment, setNewComment] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [newTimeEntry, setNewTimeEntry] = useState({ hours: '', entry_type: 'support', description: '' });
  const [editingTimeEntry, setEditingTimeEntry] = useState<any>(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStart, setTimerStart] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const showToast = (message: string, icon: 'success'|'delete'|'warning'|'info' = 'success') => {
    setSuccessMessage(message);
    setSuccessIcon(icon);
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 2000);
  };
  const currentUsername = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').username || localStorage.getItem('username') || 'admin'; }
    catch { return 'admin'; }
  })();
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successIcon, setSuccessIcon] = useState<'success'|'delete'|'warning'|'info'>('success');
  const [confirmModal, setConfirmModal] = useState<{show: boolean, title: string, message: string, color: string, onConfirm: () => void}>({show: false, title: '', message: '', color: '', onConfirm: () => {}});
  // Help guide is now a shared component
  const isClientUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').type === 'client'; } catch { return false; } })();

  const priorityColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
    urgent: '#dc2626'
  };

  const priorityTimeouts = {
    urgent: 12, // hours
    high: 24,
    medium: 48,
    low: 72
  };

  const calculateTimeRemaining = (createdAt: string, priority: string) => {
    const created = new Date(createdAt).getTime();
    const now = currentTime; // Use currentTime state instead of Date.now()
    const timeoutHours = priorityTimeouts[priority as keyof typeof priorityTimeouts] || 72;
    const timeoutMs = timeoutHours * 60 * 60 * 1000;
    const elapsed = now - created;
    const remaining = timeoutMs - elapsed;

    if (remaining <= 0) {
      return { overdue: true, display: 'OVERDUE', color: '#dc2626' };
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    let color = '#10b981'; // Green
    if (remaining < timeoutMs * 0.25) color = '#dc2626'; // Red (< 25% time left)
    else if (remaining < timeoutMs * 0.5) color = '#f59e0b'; // Orange (< 50% time left)

    return {
      overdue: false,
      display: `${hours}h ${minutes}m ${seconds}s`,
      color,
      hours,
      minutes,
      seconds
    };
  };

  const statusColors = {
    open: '#2563eb',
    in_progress: '#3b82f6',
    resolved: '#60a5fa',
    closed: '#94a3b8'
  };

  // Fetch Master Admin users
  const fetchMasterAdminUsers = async () => {
    try {
      const response = await userAPI.getAll();
      const users = response.data;
      const masterAdmins = users
        .filter((user: any) => user.role === 'master_admin')
        .map((user: any) => user.username);

      setMasterAdminUsers(masterAdmins);
      console.log('Master Admin users:', masterAdmins);
    } catch (error) {
      console.error('Error fetching Master Admin users:', error);
      // Fallback to known admin usernames
      setMasterAdminUsers(['admin', 'system', 'TomR']);
    }
  };

  const fetchTickets = useCallback(async () => {
    try {
      const params: any = {};
      if (filter.status) params.status = filter.status;
      if (filter.priority) params.priority = filter.priority;
      if (isClient && currentClientId) params.client_id = currentClientId;

      console.log('Fetching tickets with params:', params);
      const response = await ticketsAPI.getAll(params);
      const data = response.data;
      console.log('Tickets fetched:', data.length, 'tickets');
      console.log('First ticket updates:', data[0]?.updates?.length || 0, 'updates');
      console.log('First ticket data:', data[0]);
      setTickets(Array.isArray(data) ? data : []);

      // Recalculate KPIs after fetching tickets
      if (Array.isArray(data)) {
        calculateKPIsFromData(data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
    }
  }, [filter, isClient, currentClientId]);

  // Timer effect
  useEffect(() => {
    let interval: any;
    if (isTimerRunning && timerStart) {
      interval = setInterval(() => {
        setTimerSeconds(Math.floor((Date.now() - timerStart) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timerStart]);

  const fetchArchivedTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://api.camel.optitrac.com/api/tickets?include_archived=true', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const archived = (Array.isArray(data) ? data : []).filter(
        (t: any) => t.status === 'closed' || t.deleted_at
      );
      setArchivedTickets(archived);
    } catch (error) {
      console.error('Error fetching archived tickets:', error);
    }
  };
  useEffect(() => {
    fetchTickets();
    fetchClients();
  }, [fetchTickets]);

  useEffect(() => {
    // Update countdown timers every second
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Force load devices for clients on component mount
  useEffect(() => {
    fetchMasterAdminUsers();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const clientUser = JSON.parse(localStorage.getItem('clientUser') || '{}');
    const clientId = user.client_id || clientUser.client_id;
    
    if (clientId) {
      fetchClientDevices(clientId);
      // Fetch support hours for client
      const fetchSupportHours = async () => {
        try {
          const token = localStorage.getItem('token') || localStorage.getItem('clientToken');
          const now = new Date();
          const monthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          const [clientRes, usageRes] = await Promise.all([
            fetch(`https://api.camel.optitrac.com/api/clients/${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
            fetch(`https://api.camel.optitrac.com/api/time-tracking/clients/${clientId}/usage/${monthYear}`, { headers: { 'Authorization': `Bearer ${token}` } })
          ]);
          const clientData = await clientRes.json();
          const usageData = await usageRes.json();
          setSupportHours({
            allocated: clientData.monthly_hours_allocated || 0,
            used: usageData.support_hours_used || 0,
            outside: usageData.outside_support_cost || 0
          });
        } catch (e) { console.error('Error fetching support hours:', e); }
      };
      fetchSupportHours();
    }
  }, []);

  // Also trigger device fetch when client_id changes (for admins selecting client)
  useEffect(() => {
    if (newTicket.client_id && !isClient) {
      fetchClientDevices(newTicket.client_id);
    }
  }, [newTicket.client_id, isClient]);

  // Prefill ticket from fleet dashboard
  useEffect(() => {
    if (prefillTicket && devices.length > 0) {
      const match = devices.find((d: any) => d.hex_value?.toUpperCase() === prefillTicket.unitId?.toUpperCase());
      const prio = prefillTicket.issue.includes('Battery') ? 'high' : 'medium';
      setNewTicket(prev => ({
        ...prev,
        device_id: match?.id || '',
        title: `${prefillTicket.name} — ${prio}`,
        description: '',
        category: 'hardware',
        priority: prio
      }));
      setShowCreateForm(true);
    }
  }, [prefillTicket, devices]);

  // Real-time updates with polling
  useEffect(() => {
    const interval = setInterval(() => {
      if (!showCreateForm && !selectedTicket) {
        fetchTickets();
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [showCreateForm, selectedTicket, fetchTickets]);

  // Flash effect for new tickets
  const [flashingTickets, setFlashingTickets] = useState<Set<number>>(new Set());

  useEffect(() => {
    const currentTicketIds = new Set(tickets.map(t => t.id));
    const previousTicketIds = new Set(JSON.parse(localStorage.getItem('previousTicketIds') || '[]'));

    // Find new tickets
    const newTicketIds = Array.from(currentTicketIds).filter(id => !previousTicketIds.has(id));

    if (newTicketIds.length > 0 && previousTicketIds.size > 0) { // Only flash if we had previous tickets
      setFlashingTickets(new Set(newTicketIds));

      // Stop flashing after 10 seconds
      setTimeout(() => {
        setFlashingTickets(new Set());
      }, 10000);
    }

    // Store current ticket IDs for next comparison
    localStorage.setItem('previousTicketIds', JSON.stringify(Array.from(currentTicketIds)));
  }, [tickets]);

  const calculateKPIsFromData = (allTickets: any[]) => {
    // Count all tickets that are not closed and not deleted
    const openTickets = allTickets.filter(ticket => 
      ticket.status !== 'closed' && 
      !ticket.deleted_at
    ).length;

    // Calculate average resolution time for resolved/closed tickets
    const resolvedTickets = allTickets.filter(ticket =>
      (ticket.status === 'resolved' || ticket.status === 'closed') && ticket.resolved_at
    );

    let avgResolutionTime = 0;
    if (resolvedTickets.length > 0) {
      const totalResolutionTime = resolvedTickets.reduce((sum, ticket) => {
        const created = new Date(ticket.created_at).getTime();
        const resolved = new Date(ticket.resolved_at).getTime();
        return sum + (resolved - created);
      }, 0);
      avgResolutionTime = totalResolutionTime / resolvedTickets.length / (1000 * 60 * 60 * 24); // Convert to days
    }

    // Find longest open ticket (including in_progress)
    const openTicketsList = allTickets.filter(ticket => ticket.status === 'open' || ticket.status === 'in_progress');
    let longestOpenTicket = 0;
    if (openTicketsList.length > 0) {
      const now = new Date().getTime();
      longestOpenTicket = Math.max(...openTicketsList.map(ticket => {
        const created = new Date(ticket.created_at).getTime();
        return (now - created) / (1000 * 60 * 60 * 24); // Convert to days
      }));
    }

    const newStats = {
      openTickets,
      avgResolutionTime: Math.round(avgResolutionTime * 10) / 10, // Round to 1 decimal
      longestOpenTicket: Math.round(longestOpenTicket * 10) / 10 // Round to 1 decimal
    };

    setTicketStats(newStats);
  };

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll();
      const data = response.data;
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const fetchClientDevices = async (clientId: string) => {
    setDevicesLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (user.type === 'client') {
        const response = await deviceAPI.getMyDevices();
        setDevices(response.data || []);
      } else {
        const response = await deviceAPI.getByClientId(clientId);
        setDevices(response.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching devices:', error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId: number) => {
    try {
      const response = await ticketsAPI.getById(ticketId);
      const data = response.data;
      setTicketDetails(data);
      setAttachedFiles([]);
      fetchTimeEntries(ticketId);
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

      // Mark unread comments as read after 3s delay so user sees the highlights
      const username = currentUsername;
      if (data.updates) {
        const unreadIds = data.updates
          .filter((u: any) => u.created_by !== username && !u.read_by?.[username])
          .map((u: any) => u.id);
        if (unreadIds.length > 0) {
          setTimeout(() => {
            const token = localStorage.getItem('token');
            fetch(`https://api.camel.optitrac.com/api/tickets/${ticketId}/comments/read`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ commentIds: unreadIds })
            }).then(() => {
              ticketsAPI.getById(ticketId).then(r => setTicketDetails(r.data));
            });
          }, 3000);
        }
      }
      markTicketAsRead(ticketId.toString());
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    }
  };

  // Mark ticket comments as read when viewing ticket details
  const markTicketAsRead = (ticketId: string) => {
    const ticket = tickets.find(t => t.id.toString() === ticketId);
    if (ticket && ticket.updates) {
      const commentIds = ticket.updates
        .filter(update => update.update_type === 'comment')
        .map(update => `${ticketId}-${update.id}`);

      setReadComments(prev => {
        const newSet = new Set(prev);
        commentIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  // Check if ticket has unread comments
  const hasUnreadComments = (ticket: Ticket) => {
    if (!ticket.updates || ticket.updates.length === 0) return false;
    const username = currentUsername;
    return ticket.updates.some(
      (u: any) => u.created_by !== username && !u.read_by?.[username]
    );
  };

  const updateTicketStatus = async (ticketId: number, status: string) => {
    try {
      await ticketsAPI.update(ticketId, {
        status,
        updated_by: localStorage.getItem('username') || 'Admin'
      });

      fetchTickets();
      if (ticketDetails && ticketDetails.id === ticketId) {
        fetchTicketDetails(ticketId);
      }
      showToast(`Ticket ${status === 'closed' ? 'closed' : 'updated'} successfully!`);
    } catch (error) {
      console.error('Error updating ticket:', error);
      showToast('Error updating ticket', 'warning');
    }
  };

  const createTicket = async () => {
    if (!newTicket.description || !newTicket.client_id || !newTicket.device_id) {
      showToast('Please fill in all required fields including device selection', 'warning');
      return;
    }

    setLoading(true);
    try {
      // Auto-generate title: "vehicle_name — priority"
      const selectedDevice = devices.find(d => d.id === newTicket.device_id);
      const vehicleName = selectedDevice?.vehicle_id || selectedDevice?.serial_number || 'Unknown';
      const autoTitle = newTicket.title || `${vehicleName} — ${newTicket.priority}`;

      await ticketsAPI.create({
        ...newTicket,
        title: autoTitle,
        description: prefillTicket
          ? `[Vehicle: ${prefillTicket.name} | Unit: ${prefillTicket.unitId} | ${prefillTicket.platform} | ${prefillTicket.issue}]\n\n${newTicket.description}`
          : newTicket.description,
        created_by: localStorage.getItem('username') || 'Admin'
      });

      setShowCreateForm(false);
      setNewTicket({
        client_id: isClient ? currentClientId : '',
        device_id: '',
        title: '',
        description: '',
        priority: 'medium',
        category: 'general'
      });
      fetchTickets();
      showToast('Ticket created successfully!');
    } catch (error) {
      console.error('Error creating ticket:', error);
      showToast('Error creating ticket', 'warning');
    } finally {
      setLoading(false);
    }
  };

  const updateTicketPriority = async (newPriority: string) => {
    if (!selectedTicket) return;

    try {
      await ticketsAPI.update(selectedTicket.id, {
        priority: newPriority,
        updated_by: localStorage.getItem('username') || 'User'
      });

      // Update local state
      setTicketDetails((prev: any) => ({ ...prev, priority: newPriority }));
      setSelectedTicket(prev => prev ? { ...prev, priority: newPriority as any } : null);
      fetchTickets(); // Refresh the ticket list
    } catch (error) {
      console.error('Error updating priority:', error);
      showToast('Error updating ticket priority', 'warning');
    }
  };

  const addComment = async () => {
    if (!newComment.trim() && attachedFiles.length === 0) {
      showToast('Please enter a comment or attach files', 'info');
      return;
    }

    if (!selectedTicket) return;

    try {
      if (attachedFiles.length > 0) {
        const formData = new FormData();
        formData.append('comment', newComment || '');
        attachedFiles.forEach(file => {
          formData.append('files', file);
        });

        const token = localStorage.getItem('token');
        const response = await fetch(`https://api.camel.optitrac.com/api/tickets/${selectedTicket.id}/comments/files`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } else {
        await ticketsAPI.addComment(selectedTicket.id, {
          comment: newComment
        });
      }

      setNewComment('');
      setAttachedFiles([]);
      setRespondedTickets(prev => new Set(prev).add(selectedTicket.id.toString()));

      // Refresh immediately without delay
      await Promise.all([
        fetchTicketDetails(selectedTicket.id),
        fetchTickets()
      ]);

      setSuccessMessage('Comment sent successfully!');
      showToast('Comment sent successfully!');
    } catch (error: any) {
      console.error('Error adding comment:', error);
      showToast('Error adding comment', 'warning');
    }
  };

  const handleFileAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    console.log('Files selected:', files); // Debug log

    if (files.length === 0) {
      console.log('No files selected');
      return;
    }

    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!validTypes.includes(file.type)) {
        showToast(`${file.name}: Invalid file type. Use images, PDFs, or text files`, 'warning');
        return false;
      }

      if (file.size > maxSize) {
        showToast(`${file.name}: File too large. Maximum 10MB`, 'warning');
        return false;
      }

      return true;
    });

    console.log('Valid files:', validFiles); // Debug log
    setAttachedFiles(prev => {
      const newFiles = [...prev, ...validFiles];
      console.log('Updated attached files:', newFiles); // Debug log
      return newFiles;
    });

    // Clear the input so the same file can be selected again
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const newWindow = window.open(url, '_blank');

    // Clean up the URL after a delay to free memory
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);

    if (!newWindow) {
      showToast('Please allow popups to view files', 'info');
    }
  };

  const openHistoricalFile = (filename: string, originalName: string) => {
    const token = localStorage.getItem('token');
    const url = `https://api.camel.optitrac.com/api/tickets/files?key=${encodeURIComponent(filename)}`;
    
    fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(data => {
      window.open(data.url, '_blank');
    })
    .catch(error => {
      console.error('Error opening file:', error);
      showToast('Error opening file', 'warning');
    });
  };

  const deleteTicket = async (ticketId: number) => {
    setConfirmModal({
      show: true,
      title: 'Delete Ticket',
      message: 'Are you sure you want to delete this ticket? This action will be recorded in the history.',
      color: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(prev => ({...prev, show: false}));
        try {
          await ticketsAPI.delete(ticketId);
          setSelectedTicket(null);
          setTicketDetails(null);
          fetchTickets();
          showToast('Ticket deleted successfully', 'delete');
        } catch (error) {
          console.error('Error deleting ticket:', error);
          showToast('Error deleting ticket', 'warning');
        }
      }
    });
  };

  const reactivateTicket = async (ticketId: number) => {
    setConfirmModal({
      show: true,
      title: 'Reactivate Ticket',
      message: 'This will reopen the ticket and move it back to active tickets.',
      color: '#10b981',
      onConfirm: async () => {
        setConfirmModal(prev => ({...prev, show: false}));
        try {
          const token = localStorage.getItem('token');
          await fetch(`https://api.camel.optitrac.com/api/tickets/${ticketId}/reactivate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          fetchTickets();
          fetchArchivedTickets();
          fetchTicketDetails(ticketId);
          showToast('Ticket reactivated successfully!');
        } catch (error) {
          showToast('Error reactivating ticket', 'warning');
        }
      }
    });
  };

  const archiveTicket = async (ticketId: number) => {
    setConfirmModal({
      show: true,
      title: 'Archive Ticket',
      message: 'This will move the ticket to archived. You can reactivate it later if needed.',
      color: '#6b7280',
      onConfirm: async () => {
        setConfirmModal(prev => ({...prev, show: false}));
        try {
          const token = localStorage.getItem('token') || localStorage.getItem('clientToken');
          await fetch(`https://api.camel.optitrac.com/api/tickets/${ticketId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          fetchTickets();
          fetchArchivedTickets();
          setSelectedTicket(null);
          setTicketDetails(null);
          showToast('Ticket archived successfully!');
        } catch (error) {
          showToast('Error archiving ticket', 'warning');
        }
      }
    });
  };

  const fetchTimeEntries = async (ticketId: number) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('clientToken');
      const response = await fetch(`https://api.camel.optitrac.com/api/time-tracking/tickets/${ticketId}/time`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTimeEntries(data.timeEntries || []);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const addTimeEntry = async () => {
    if (!ticketDetails || (!newTimeEntry.hours && !isTimerRunning)) {
      showToast('Please enter hours or use timer', 'warning');
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('clientToken');
      const hours = isTimerRunning ? (timerSeconds / 3600).toFixed(2) : newTimeEntry.hours;
      
      // Get client's hourly rate
      const clientResponse = await fetch(`https://api.camel.optitrac.com/api/clients/${ticketDetails.client_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const clientData = await clientResponse.json();
      const hourlyRate = clientData.hourly_rate || 0;

      await fetch(`https://api.camel.optitrac.com/api/time-tracking/tickets/${ticketDetails.id}/time`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          hours: parseFloat(hours),
          entry_type: newTimeEntry.entry_type,
          description: newTimeEntry.description,
          client_id: ticketDetails.client_id,
          hourly_rate: hourlyRate
        })
      });

      setNewTimeEntry({ hours: '', entry_type: 'support', description: '' });
      setIsTimerRunning(false);
      setTimerStart(null);
      setTimerSeconds(0);
      fetchTimeEntries(ticketDetails.id);
      showToast('Time entry added successfully!');
    } catch (error) {
      console.error('Error adding time entry:', error);
      showToast('Error adding time entry', 'warning');
    }
  };

  const updateTimeEntry = async (entryId: string) => {
    if (!editingTimeEntry || !editingTimeEntry.hours) {
      showToast('Please enter hours', 'warning');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await fetch(`https://api.camel.optitrac.com/api/time-tracking/entries/${entryId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          hours: parseFloat(editingTimeEntry.hours),
          entry_type: editingTimeEntry.entry_type,
          description: editingTimeEntry.description
        })
      });

      setEditingTimeEntry(null);
      fetchTimeEntries(ticketDetails.id);
      showToast('Time entry updated!');
    } catch (error) {
      console.error('Error updating time entry:', error);
      showToast('Error updating time entry', 'warning');
    }
  };

  const deleteTimeEntry = async (entryId: string) => {
    if (!window.confirm('Delete this time entry?')) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`https://api.camel.optitrac.com/api/time-tracking/entries/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchTimeEntries(ticketDetails.id);
      showToast('Time entry deleted', 'delete');
    } catch (error) {
      console.error('Error deleting time entry:', error);
      showToast('Error deleting time entry', 'warning');
    }
  };

  const startTimer = () => {
    setIsTimerRunning(true);
    setTimerStart(Date.now());
    setTimerSeconds(0);
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    const hours = (timerSeconds / 3600).toFixed(2);
    setNewTimeEntry({ ...newTimeEntry, hours });
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', margin: 0, color: '#111827', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '22px' }}>🎫</span>
            Support Tickets
          </h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '13px' }}>Manage support requests and device issues</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
        >
          + New Ticket
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab('active')}
          style={{
            padding: '10px 18px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
            background: activeTab === 'active' ? '#2563eb' : '#f3f4f6',
            color: activeTab === 'active' ? '#fff' : '#555'
          }}
        >
          🎫 Active ({tickets.length})
        </button>
        <button
          onClick={() => { setActiveTab('archived'); fetchArchivedTickets(); }}
          style={{
            padding: '10px 18px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
            background: activeTab === 'archived' ? '#2563eb' : '#f3f4f6',
            color: activeTab === 'archived' ? '#fff' : '#555'
          }}
        >
          📦 Archived ({archivedTickets.length})
        </button>
      </div>

      {/* Support Hours Widget - Client Only */}
      {isClient && supportHours && (
        <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', border: '1px solid #bfdbfe' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>⏱️</span>
              <div>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e40af' }}>Support Hours - {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                <div style={{ fontSize: '12px', color: '#3b82f6' }}>Monthly allocation</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#2563eb' }}>{supportHours.used.toFixed(1)} / {supportHours.allocated} hrs</div>
              <div style={{ fontSize: '12px', color: supportHours.allocated - supportHours.used >= 0 ? '#16a34a' : '#dc2626', fontWeight: '600' }}>
                {supportHours.allocated - supportHours.used >= 0 ? `${(supportHours.allocated - supportHours.used).toFixed(1)}h remaining` : `${Math.abs(supportHours.allocated - supportHours.used).toFixed(1)}h over`}
              </div>
            </div>
          </div>
          {supportHours.allocated > 0 && (
            <div style={{ marginTop: '12px', height: '8px', backgroundColor: '#e0e7ff', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min((supportHours.used / supportHours.allocated) * 100, 100)}%`,
                height: '100%',
                background: (supportHours.used / supportHours.allocated) > 0.9 ? '#ef4444' : (supportHours.used / supportHours.allocated) > 0.75 ? '#f59e0b' : '#2563eb',
                borderRadius: '4px'
              }} />
            </div>
          )}
          {supportHours.outside > 0 && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#ea580c', fontWeight: '600' }}>💰 Outside support: ${supportHours.outside.toFixed(2)}</div>
          )}
        </div>
      )}

      {activeTab === 'active' && (<>
      {/* Priority Dashboard */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {(() => {
          const active = tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved');
          const unread = tickets.filter(ticket => hasUnreadComments(ticket)).length;
          const tiles = [
            { label: 'Open', value: ticketStats.openTickets, icon: '🎫', color: '#2563eb', bg: '#eff6ff' },
            { label: 'Urgent', value: active.filter(t => t.priority === 'urgent').length, icon: '🚨', color: '#dc2626', bg: '#fef2f2', pulse: true },
            { label: 'High', value: active.filter(t => t.priority === 'high').length, icon: '⚠️', color: '#ea580c', bg: '#fff7ed' },
            { label: 'Medium', value: active.filter(t => t.priority === 'medium').length, icon: '📋', color: '#f59e0b', bg: '#fffbeb' },
            { label: 'Low', value: active.filter(t => t.priority === 'low').length, icon: '📝', color: '#3b82f6', bg: '#eff6ff' },
            { label: 'New Comments', value: unread, icon: '💬', color: '#10b981', bg: '#ecfdf5', pulse: unread > 0 },
          ];
          return tiles.map((t, i) => (
            <div key={i} style={{
              background: t.bg,
              borderRadius: '10px', 
              padding: '14px 10px', 
              textAlign: 'center',
              border: `1px solid ${t.color}20`
            }}>
              <div style={{ fontSize: '20px', marginBottom: '4px' }}>{t.icon}</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: t.color }}>{t.value}</div>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>{t.label}</div>
            </div>
          ));
        })()}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '20px' }}>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', background: 'white' }}
        >
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select
          value={filter.priority}
          onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
          style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', background: 'white' }}
        >
          <option value="">All Priority</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      {/* Create Ticket Form */}
      {showCreateForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '480px', maxWidth: '95%', maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🎫 New Support Ticket
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>We'll get back to you shortly</p>
              </div>
              <button onClick={() => { setShowCreateForm(false); setAttachedFiles([]); }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', fontSize: '18px', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            {/* Form Body */}
            <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* Prefill Banner */}
                {prefillTicket && (
                  <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '2px solid #3b82f6', borderRadius: '12px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '28px' }}>{prefillTicket.platform === 'Camel' ? '🐫' : '⭐'}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e40af' }}>{prefillTicket.name}</div>
                      <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '2px' }}>Unit: {prefillTicket.unitId} • {prefillTicket.platform}</div>
                    </div>
                  </div>
                )}

                {/* Client Select (Admin only) */}
                {!isClient && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>👤 Client</label>
                    <select
                      value={newTicket.client_id}
                      onChange={(e) => { setNewTicket({ ...newTicket, client_id: e.target.value, device_id: '' }); setDeviceSearch(''); }}
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '14px', backgroundColor: 'white', boxSizing: 'border-box' }}
                    >
                      <option value="">Select Client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.name} - {client.company_name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Vehicle Selector */}
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>📍 Device</label>
                  
                  {newTicket.device_id ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: '10px', border: '2px solid #2563eb', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' }}>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e40af' }}>{devices.find(d => d.id === newTicket.device_id)?.vehicle_id || 'Device'}</div>
                        <div style={{ fontSize: '12px', color: '#3b82f6' }}>{devices.find(d => d.id === newTicket.device_id)?.serial_number}</div>
                      </div>
                      <button onClick={() => { setNewTicket({ ...newTicket, device_id: '' }); setDeviceSearch(''); }} style={{ background: 'white', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>Change</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', opacity: 0.5 }}>🔍</span>
                        <input
                          type="text"
                          placeholder={!newTicket.client_id ? 'Select client first...' : devicesLoading ? 'Loading devices...' : 'Search devices...'}
                          value={deviceSearch}
                          onChange={(e) => { setDeviceSearch(e.target.value); setShowDeviceDropdown(true); }}
                          onFocus={() => setShowDeviceDropdown(true)}
                          disabled={!newTicket.client_id || devicesLoading}
                          style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '14px', backgroundColor: 'white', boxSizing: 'border-box' }}
                        />
                        {devicesLoading && (
                          <div style={{ position: 'absolute', bottom: '0', left: '0', right: '0', height: '3px', backgroundColor: '#e5e7eb', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', backgroundColor: '#3b82f6', animation: 'loading 1.5s ease-in-out infinite', width: '40%' }}></div>
                          </div>
                        )}
                      </div>
                      
                      {showDeviceDropdown && devices.length > 0 && (
                        <>
                          <div onClick={() => setShowDeviceDropdown(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} />
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', backgroundColor: 'white', borderRadius: '12px', border: '2px solid #e5e7eb', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', zIndex: 999, maxHeight: '240px', overflowY: 'auto' }}>
                            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>
                              {devices.filter(d => { const s = deviceSearch.toLowerCase(); return !s || d.vehicle_id?.toLowerCase().includes(s) || d.serial_number?.toLowerCase().includes(s); }).length} devices
                            </div>
                            {devices.filter(d => { const s = deviceSearch.toLowerCase(); return !s || d.vehicle_id?.toLowerCase().includes(s) || d.serial_number?.toLowerCase().includes(s); }).map(device => (
                              <div key={device.id} onClick={() => { setNewTicket({ ...newTicket, device_id: device.id }); setDeviceSearch(''); setShowDeviceDropdown(false); }}
                                style={{ padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>{device.vehicle_id || device.serial_number}</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>SN: {device.serial_number}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Category & Priority Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>📁 Category</label>
                    <select
                      value={newTicket.category}
                      onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '14px', backgroundColor: 'white', boxSizing: 'border-box' }}
                    >
                      <option value="general">General Support</option>
                      <option value="hardware">Hardware Issue</option>
                      <option value="software">Software Issue</option>
                      <option value="connectivity">Connectivity</option>
                      <option value="billing">Billing</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>⚡ Priority</label>
                    <select
                      value={newTicket.priority}
                      onChange={(e) => { const p = e.target.value; setNewTicket(prev => ({ ...prev, priority: p, title: prefillTicket ? `${prefillTicket.name} — ${p}` : prev.title })); }}
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '14px', backgroundColor: 'white', boxSizing: 'border-box' }}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                {/* Title (optional - auto-generated from vehicle + priority) */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>✏️ Title <span style={{ fontWeight: '400', color: '#9ca3af' }}>(optional — auto-generates as "{devices.find(d => d.id === newTicket.device_id)?.vehicle_id || 'vehicle'} — {newTicket.priority}")</span></label>
                  <input
                    type="text"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                    placeholder={newTicket.device_id ? `${devices.find(d => d.id === newTicket.device_id)?.vehicle_id || ''} — ${newTicket.priority}` : 'Select a device first...'}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Description */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>📝 Description</label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    placeholder="Please describe the issue in detail..."
                    rows={4}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                </div>

                {/* Attachments */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>📎 Attachments</label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#f3f4f6', color: '#374151', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', border: '2px dashed #d1d5db' }}>
                    <span>📎</span> Add Files
                    <input type="file" multiple accept="image/*,.pdf,.txt" onChange={handleFileAttachment} style={{ display: 'none' }} />
                  </label>
                  {attachedFiles.length > 0 && (
                    <div style={{ marginTop: '10px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                      {attachedFiles.map((file, index) => (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: '13px', color: '#374151' }}>
                          <span>📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                          <button onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))} style={{ background: '#fee2e2', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '500' }}>Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateForm(false)} style={{ padding: '12px 20px', borderRadius: '10px', border: '2px solid #e5e7eb', backgroundColor: 'white', color: '#6b7280', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createTicket} disabled={loading} style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: loading ? '#9ca3af' : 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', fontSize: '14px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? '⏳ Creating...' : '✓ Submit Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tickets List */}
      <div style={{ display: 'grid', gap: '14px' }}>
        {tickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px', backgroundColor: 'white', borderRadius: '10px', border: '1px dashed #d1d5db' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px', opacity: 0.3 }}>🎫</div>
            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>No tickets found</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {tickets.map((ticket) => {
              const timeRemaining = (ticket.status === 'open' || ticket.status === 'in_progress') ? calculateTimeRemaining(ticket.created_at, ticket.priority) : null;

              // Check if ticket has unread client comments
              const hasNewClientComments = hasUnreadComments(ticket);

              return (
                <div
                  key={ticket.id}
                  onClick={() => { setSelectedTicket(ticket); fetchTicketDetails(ticket.id); }}
                  style={{
                    padding: '16px 18px',
                    borderLeft: `4px solid ${ticket.priority === 'urgent' ? '#dc2626' : ticket.priority === 'high' ? '#f59e0b' : hasNewClientComments ? '#10b981' : '#2563eb'}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: hasNewClientComments ? '#f0fdf4' : ticket.priority === 'urgent' ? '#fef2f2' : ticket.priority === 'high' ? '#fff7ed' : 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    position: 'relative'
                  }}
                >
                  {hasNewClientComments && (
                    <span style={{ position: 'absolute', top: '12px', right: '12px', background: '#10b981', color: 'white', padding: '4px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>💬 NEW</span>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div style={{ flex: 1, paddingRight: hasNewClientComments ? '70px' : '0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '3px 8px', borderRadius: '4px' }}>#{ticket.id}</span>
                        <span style={{ fontWeight: '600', fontSize: '15px', color: '#111827' }}>{ticket.title}</span>
                      </div>
                      <p style={{ margin: '0', color: '#6b7280', fontSize: '13px' }}>
                        👤 {ticket.client_name || ticket.client_id || 'Loading...'}{ticket.created_by ? ` · ${ticket.created_by}` : ''}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {timeRemaining && (
                        <span style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: timeRemaining.color, color: 'white' }}>
                          ⏱️ {timeRemaining.display}
                        </span>
                      )}
                      <span style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: priorityColors[ticket.priority], color: 'white' }}>
                        {ticket.priority.toUpperCase()}
                      </span>
                      <span style={{ padding: '4px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: statusColors[ticket.status], color: 'white' }}>
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <p style={{ margin: '0 0 10px 0', color: '#4b5563', fontSize: '13px', lineHeight: '1.5' }}>
                    {ticket.description.length > 100 ? ticket.description.substring(0, 100) + '...' : ticket.description}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#9ca3af' }}>
                    <div style={{ display: 'flex', gap: '14px' }}>
                      <span>📅 {new Date(ticket.created_at).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}</span>
                      <span>🏷️ {ticket.category}</span>
                    </div>
                    <span style={{ color: '#2563eb', fontWeight: '600' }}>View →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      </>)}

      {/* Ticket Details Modal */}
      {selectedTicket && ticketDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '900px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e5e7eb'
          }}>
            {/* Professional Header */}
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
              padding: '24px 32px',
              color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      padding: '8px',
                      borderRadius: '8px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <span style={{ fontSize: '20px' }}>🎫</span>
                    </div>
                    <div>
                      <h2 style={{
                        margin: 0,
                        fontSize: '24px',
                        fontWeight: '700',
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        Ticket #{ticketDetails.id}
                      </h2>
                      <p style={{
                        margin: '4px 0 0 0',
                        fontSize: '16px',
                        opacity: 0.9,
                        fontWeight: '400'
                      }}>
                        {ticketDetails.vehicle_name && !ticketDetails.title.includes(ticketDetails.vehicle_name) 
                          ? `${ticketDetails.vehicle_name} — ${ticketDetails.title}` 
                          : ticketDetails.title}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {ticketDetails.status.replace('_', ' ')}
                    </span>
                    <span style={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backdropFilter: 'blur(10px)'
                    }}>
                      {ticketDetails.priority.toUpperCase()} PRIORITY
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* Delete button temporarily disabled - will be re-enabled after testing */}
                  <button
                    onClick={() => { 
                      setSelectedTicket(null); 
                      setTicketDetails(null); 
                      setAttachedFiles([]);
                      setNewComment('');
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '8px',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      cursor: 'pointer',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Content Area */}
              <div style={{
                padding: '32px',
                maxHeight: 'calc(90vh - 120px)',
                overflowY: 'auto',
                backgroundColor: '#fafafa'
              }}>

                {/* Professional Ticket Summary */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '20px',
                    paddingBottom: '16px',
                    borderBottom: '2px solid #f3f4f6'
                  }}>
                    <div style={{
                      backgroundColor: '#2563eb',
                      borderRadius: '8px',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ color: 'white', fontSize: '16px' }}>📋</span>
                    </div>
                    <h4 style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1f2937'
                    }}>
                      Ticket Information
                    </h4>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px'
                  }}>
                    <div style={{
                      backgroundColor: '#f8fafc',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>CLIENT</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                        {ticketDetails.client_name || 'Loading...'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        {ticketDetails.company_name || ticketDetails.client_id}
                      </div>
                    </div>

                    <div style={{
                      backgroundColor: '#f8fafc',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>CREATED</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                        {new Date(ticketDetails.created_at).toLocaleDateString('en-NZ', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        {new Date(ticketDetails.created_at).toLocaleTimeString('en-NZ', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>

                    <div style={{
                      backgroundColor: '#f8fafc',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>PRIORITY</div>
                      {(ticketDetails.status === 'open' || ticketDetails.status === 'in_progress') ? (
                        <select
                          value={ticketDetails.priority}
                          onChange={(e) => updateTicketPriority(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '14px',
                            fontWeight: '600',
                            backgroundColor: 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="low">🟢 Low Priority</option>
                          <option value="medium">🟡 Medium Priority</option>
                          <option value="high">🔴 High Priority</option>
                        </select>
                      ) : (
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                            {ticketDetails.priority === 'low' ? '🟢 Low' :
                              ticketDetails.priority === 'medium' ? '🟡 Medium' : '🔴 High'}
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>Priority Level</div>
                        </div>
                      )}
                    </div>

                    <div style={{
                      backgroundColor: '#f8fafc',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>ASSIGNED TO</div>
                      {!isClient ? (
                        <select
                          value={ticketDetails.assigned_to || ''}
                          onChange={async (e) => {
                            const val = e.target.value;
                            await ticketsAPI.update(ticketDetails.id, { assigned_to: val });
                            setTicketDetails({ ...ticketDetails, assigned_to: val });
                            fetchTickets();
                          }}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '14px', fontWeight: '600', color: '#1e293b', backgroundColor: 'white', cursor: 'pointer' }}
                        >
                          <option value="">Unassigned</option>
                          {masterAdminUsers.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      ) : (
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                          {ticketDetails.assigned_to || 'Unassigned'}
                        </div>
                      )}
                      <div style={{ fontSize: '13px', color: '#64748b' }}>Support Agent</div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>Description</h4>
                  <p style={{ margin: 0, padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '14px' }}>
                    {ticketDetails.description}
                  </p>
                </div>

                {/* Modern Communication History */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '20px',
                    paddingBottom: '16px',
                    borderBottom: '2px solid #f3f4f6'
                  }}>
                    <div style={{
                      backgroundColor: '#2563eb',
                      borderRadius: '8px',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ color: 'white', fontSize: '16px' }}>💬</span>
                    </div>
                    <h4 style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1f2937'
                    }}>
                      Communication History
                    </h4>
                    {ticketDetails.updates && ticketDetails.updates.length > 0 && (
                      <span style={{
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {ticketDetails.updates.length} messages
                      </span>
                    )}
                  </div>

                  {ticketDetails.updates && ticketDetails.updates.length > 0 ? (
                    <div style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      paddingRight: '8px'
                    }}>
                      {ticketDetails.updates
                        .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                        .map((update: any, index: number) => (
                          <div key={update.id} style={{
                            marginBottom: '16px',
                            position: 'relative'
                          }}>
                            {/* Timeline Line */}
                            {index < ticketDetails.updates.length - 1 && (
                              <div style={{
                                position: 'absolute',
                                left: '20px',
                                top: '48px',
                                bottom: '-16px',
                                width: '2px',
                                backgroundColor: '#e5e7eb'
                              }} />
                            )}

                            <div style={{
                              display: 'flex',
                              gap: '12px',
                              alignItems: 'flex-start'
                            }}>
                              {/* Avatar */}
                              <div style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#3b82f6',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                border: '3px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                zIndex: 1,
                                position: 'relative'
                              }}>
                                <span style={{
                                  color: 'white',
                                  fontSize: '16px',
                                  fontWeight: 'bold'
                                }}>
                                  {update.created_by.charAt(0).toUpperCase()}
                                </span>
                              </div>

                              {/* Message Content */}
                              <div style={{
                                flex: 1,
                                backgroundColor: (() => {
                                  const username = currentUsername;
                                  const isOwnMessage = update.created_by === username;
                                  const isUnread = !isOwnMessage && !update.read_by?.[username];
                                  return isUnread ? '#eff6ff' : '#f8fafc';
                                })(),
                                borderRadius: '16px',
                                padding: '16px',
                                border: (() => {
                                  const username = currentUsername;
                                  const isOwnMessage = update.created_by === username;
                                  const isUnread = !isOwnMessage && !update.read_by?.[username];
                                  return isUnread ? '2px solid #3b82f6' : '1px solid #e2e8f0';
                                })(),
                                position: 'relative',
                                transition: 'all 0.3s ease'
                              }}>
                                {/* Unread badge */}
                                {(() => {
                                  const username = currentUsername;
                                  const isUnread = update.created_by !== username && !update.read_by?.[username];
                                  return isUnread ? (
                                    <div style={{
                                      position: 'absolute', top: '-8px', right: '12px',
                                      backgroundColor: '#3b82f6', color: 'white',
                                      fontSize: '10px', fontWeight: '700',
                                      padding: '2px 8px', borderRadius: '10px',
                                      boxShadow: '0 2px 4px rgba(59,130,246,0.4)'
                                    }}>NEW</div>
                                  ) : null;
                                })()}
                                {/* Speech bubble arrow */}
                                <div style={{
                                  position: 'absolute',
                                  left: '-8px',
                                  top: '16px',
                                  width: 0,
                                  height: 0,
                                  borderTop: '8px solid transparent',
                                  borderBottom: '8px solid transparent',
                                  borderRight: '8px solid #f8fafc'
                                }} />

                                {/* Header */}
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '8px'
                                }}>
                                  <div style={{
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    color: '#1f2937'
                                  }}>
                                    {update.created_by}
                                  </div>
                                  <div style={{
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    backgroundColor: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb'
                                  }}>
                                    {new Date(update.created_at).toLocaleDateString('en-NZ', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>

                                {/* Message */}
                                <div style={{
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                  color: '#374151',
                                  marginBottom: update.files && update.files.length > 0 ? '12px' : '0'
                                }}>
                                  {update.content}
                                </div>

                                {/* File Attachments */}
                                {update.files && update.files.length > 0 && (
                                  <div style={{
                                    backgroundColor: 'white',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      marginBottom: '8px'
                                    }}>
                                      <span style={{ fontSize: '14px' }}>📎</span>
                                      <span style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#6b7280'
                                      }}>
                                        {update.files.length} attachment{update.files.length > 1 ? 's' : ''}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                      {update.files.map((file: any) => (
                                        <button
                                          key={file.id}
                                          onClick={() => openHistoricalFile(file.filename, file.original_name)}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 10px',
                                            backgroundColor: '#dbeafe',
                                            border: '1px solid #3b82f6',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            color: '#1e40af',
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            fontWeight: '500',
                                            transition: 'all 0.2s'
                                          }}
                                          onMouseOver={(e) => {
                                            e.currentTarget.style.backgroundColor = '#3b82f6';
                                            e.currentTarget.style.color = 'white';
                                          }}
                                          onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = '#dbeafe';
                                            e.currentTarget.style.color = '#1e40af';
                                          }}
                                          title={`${file.original_name} (${(file.file_size / 1024).toFixed(1)} KB)`}
                                        >
                                          <span>📄</span>
                                          <span>{file.original_name}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Read receipts */}
                                {update.read_by && Object.keys(update.read_by).length > 0 && (
                                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                                    <span style={{ fontSize: '12px', color: '#3b82f6' }}>✓✓</span>
                                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                      Seen by {Object.entries(update.read_by).map(([user, time]: [string, any]) =>
                                        `${user} at ${new Date(time).toLocaleString('en-NZ', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                                      ).join(', ')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      <div ref={commentsEndRef} />
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '12px',
                      border: '2px dashed #d1d5db'
                    }}>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px auto'
                      }}>
                        <span style={{ fontSize: '24px' }}>💬</span>
                      </div>
                      <h5 style={{
                        color: '#6b7280',
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0 0 8px 0'
                      }}>
                        No messages yet
                      </h5>
                      <p style={{
                        color: '#9ca3af',
                        fontSize: '14px',
                        margin: 0
                      }}>
                        Start the conversation by adding a comment below
                      </p>
                    </div>
                  )}
                </div>

                {/* Time Tracking Section - Admin Only */}
                {!isClientUser && (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '24px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '20px',
                      paddingBottom: '16px',
                      borderBottom: '2px solid #f3f4f6'
                    }}>
                      <div style={{
                        backgroundColor: '#2563eb',
                        borderRadius: '8px',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span style={{ color: 'white', fontSize: '16px' }}>⏱️</span>
                      </div>
                      <h4 style={{
                        margin: 0,
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#1f2937'
                      }}>
                        Time Tracking
                      </h4>
                      {timeEntries.length > 0 && (
                        <span style={{
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {timeEntries.reduce((sum, e) => sum + e.hours, 0).toFixed(2)} hours logged
                        </span>
                      )}
                    </div>

                    {/* Existing Time Entries */}
                    {timeEntries.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        {timeEntries.map((entry: any) => (
                          <div key={entry.id} style={{
                            padding: '12px',
                            backgroundColor: entry.entry_type === 'support' ? '#eff6ff' : '#fff7ed',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            border: `2px solid ${entry.entry_type === 'support' ? '#bae6fd' : '#fed7aa'}`
                          }}>
                            {editingTimeEntry?.id === entry.id ? (
                              <div>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                  <input
                                    type="number"
                                    step="0.25"
                                    value={editingTimeEntry.hours}
                                    onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, hours: e.target.value })}
                                    style={{ flex: 1, padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}
                                    placeholder="Hours"
                                  />
                                  <select
                                    value={editingTimeEntry.entry_type}
                                    onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, entry_type: e.target.value })}
                                    style={{ padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}
                                  >
                                    <option value="support">Support Hours</option>
                                    <option value="outside">Outside Support</option>
                                  </select>
                                </div>
                                <input
                                  type="text"
                                  value={editingTimeEntry.description}
                                  onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, description: e.target.value })}
                                  style={{ width: '100%', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '14px', marginBottom: '8px' }}
                                  placeholder="Description"
                                />
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button onClick={() => updateTimeEntry(entry.id)} style={{ flex: 1, padding: '8px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                                    Save
                                  </button>
                                  <button onClick={() => setEditingTimeEntry(null)} style={{ flex: 1, padding: '8px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      backgroundColor: entry.entry_type === 'support' ? '#2563eb' : '#ea580c',
                                      color: 'white'
                                    }}>
                                      {entry.entry_type === 'support' ? 'SUPPORT' : 'OUTSIDE'}
                                    </span>
                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                                      {entry.hours} hours
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                      by {entry.user_email}
                                    </span>
                                  </div>
                                  {entry.description && (
                                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#4b5563' }}>
                                      {entry.description}
                                    </p>
                                  )}
                                </div>
                                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                                  <div style={{ fontSize: '16px', fontWeight: '700', color: entry.entry_type === 'support' ? '#2563eb' : '#ea580c' }}>
                                    ${entry.total_cost.toFixed(2)}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    {new Date(entry.created_at).toLocaleDateString()}
                                  </div>
                                  {!isClient && (
                                    <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                      <button onClick={() => setEditingTimeEntry({ ...entry })} style={{ padding: '4px 8px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                                        Edit
                                      </button>
                                      <button onClick={() => deleteTimeEntry(entry.id)} style={{ padding: '4px 8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Time Entry Form */}

                    {/* Add Time Entry Form */}
                    <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '2px dashed #e5e7eb' }}>
                      <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700', color: '#374151' }}>
                        Log Time
                      </h5>
                      
                      {/* Timer */}
                      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{
                          flex: 1,
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '2px solid #e5e7eb',
                          fontSize: '24px',
                          fontWeight: '700',
                          textAlign: 'center',
                          color: isTimerRunning ? '#2563eb' : '#6b7280',
                          fontFamily: 'monospace'
                        }}>
                          {formatTime(timerSeconds)}
                        </div>
                        {!isTimerRunning ? (
                          <button
                            onClick={startTimer}
                            style={{
                              padding: '12px 20px',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                            }}
                          >
                            ▶ Start
                          </button>
                        ) : (
                          <button
                            onClick={stopTimer}
                            style={{
                              padding: '12px 20px',
                              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                            }}
                          >
                            ⏸ Stop
                          </button>
                        )}
                      </div>

                      {/* Manual Hours Input */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                          Hours (or use timer)
                        </label>
                        <input
                          type="number"
                          step="0.25"
                          value={newTimeEntry.hours}
                          onChange={(e) => setNewTimeEntry({ ...newTimeEntry, hours: e.target.value })}
                          disabled={isTimerRunning}
                          placeholder="e.g., 2.5"
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb',
                            fontSize: '14px'
                          }}
                        />
                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                          Quick reference: 15min = 0.25, 30min = 0.5, 45min = 0.75, 1hr = 1.0
                        </div>
                      </div>

                      {/* Type Selection */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                          Type
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => setNewTimeEntry({ ...newTimeEntry, entry_type: 'support' })}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '8px',
                              border: newTimeEntry.entry_type === 'support' ? '2px solid #2563eb' : '2px solid #e5e7eb',
                              backgroundColor: newTimeEntry.entry_type === 'support' ? '#eff6ff' : 'white',
                              color: newTimeEntry.entry_type === 'support' ? '#2563eb' : '#6b7280',
                              fontSize: '14px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            Support Hours
                          </button>
                          <button
                            onClick={() => setNewTimeEntry({ ...newTimeEntry, entry_type: 'outside' })}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '8px',
                              border: newTimeEntry.entry_type === 'outside' ? '2px solid #ea580c' : '2px solid #e5e7eb',
                              backgroundColor: newTimeEntry.entry_type === 'outside' ? '#fff7ed' : 'white',
                              color: newTimeEntry.entry_type === 'outside' ? '#ea580c' : '#6b7280',
                              fontSize: '14px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            Outside Support
                          </button>
                        </div>
                      </div>

                      {/* Description */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                          Description
                        </label>
                        <textarea
                          value={newTimeEntry.description}
                          onChange={(e) => setNewTimeEntry({ ...newTimeEntry, description: e.target.value })}
                          placeholder="What did you work on?"
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb',
                            fontSize: '14px',
                            resize: 'vertical'
                          }}
                        />
                      </div>

                      {/* Add Button */}
                      <button
                        onClick={addTimeEntry}
                        disabled={!newTimeEntry.hours && !isTimerRunning}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: (!newTimeEntry.hours && !isTimerRunning) ? '#9ca3af' : 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontWeight: '700',
                          cursor: (!newTimeEntry.hours && !isTimerRunning) ? 'not-allowed' : 'pointer',
                          boxShadow: (!newTimeEntry.hours && !isTimerRunning) ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.3)'
                        }}
                      >
                        ✓ Log Time
                      </button>
                    </div>
                  </div>
                )}

                {/* Add Comment Section */}
                <div style={{ marginBottom: '20px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>Add Comment</h4>

                  <div style={{ marginBottom: '16px' }}>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Type your comment here..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {attachedFiles.length > 0 && (
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '2px solid #bae6fd' }}>
                      <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e40af', marginBottom: '8px' }}>
                        📎 {attachedFiles.length} file(s) attached
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {attachedFiles.map((file, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <span style={{ fontWeight: '500', color: '#1e40af' }}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                            <button
                              onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
                              style={{
                                background: '#fee2e2',
                                border: 'none',
                                color: '#dc2626',
                                cursor: 'pointer',
                                fontSize: '11px',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontWeight: '600'
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button
                      onClick={addComment}
                      disabled={!newComment.trim() && attachedFiles.length === 0}
                      style={{
                        padding: '12px 24px',
                        background: (!newComment.trim() && attachedFiles.length === 0) ? '#9ca3af' : 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontWeight: '700',
                        cursor: (!newComment.trim() && attachedFiles.length === 0) ? 'not-allowed' : 'pointer',
                        boxShadow: (!newComment.trim() && attachedFiles.length === 0) ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.3)'
                      }}
                    >
                      Send Comment
                    </button>

                    <label style={{
                      padding: '12px 20px',
                      backgroundColor: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#6b7280'
                    }}>
                      📎 Attach Files
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,.txt"
                        onChange={handleFileAttachment}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                </div>

                {/* Actions */}
                {ticketDetails.status !== 'closed' && !ticketDetails.deleted_at && (
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
                    <button
                      onClick={() => {
                        if (newComment.trim() || attachedFiles.length > 0) {
                          addComment();
                        } else {
                          showToast('Please add a comment or note before updating', 'info');
                        }
                      }}
                      style={{
                        padding: '12px 20px',
                        background: 'white',
                        color: '#2563eb',
                        border: '2px solid #2563eb',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      📝 Update Ticket
                    </button>
                    <button
                      onClick={() => updateTicketStatus(ticketDetails.id, 'in_progress')}
                      disabled={ticketDetails.status === 'in_progress'}
                      style={{
                        padding: '12px 20px',
                        background: ticketDetails.status === 'in_progress' ? '#e5e7eb' : 'white',
                        color: ticketDetails.status === 'in_progress' ? '#9ca3af' : '#2563eb',
                        border: '2px solid #2563eb',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: ticketDetails.status === 'in_progress' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Mark In Progress
                    </button>
                    <button
                      onClick={() => updateTicketStatus(ticketDetails.id, 'resolved')}
                      disabled={ticketDetails.status === 'resolved'}
                      style={{
                        padding: '12px 20px',
                        background: ticketDetails.status === 'resolved' ? '#e5e7eb' : 'white',
                        color: ticketDetails.status === 'resolved' ? '#9ca3af' : '#2563eb',
                        border: '2px solid #2563eb',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: ticketDetails.status === 'resolved' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Mark Resolved
                    </button>
                    <button
                      onClick={() => {
                        setConfirmModal({
                          show: true,
                          title: 'Close Ticket',
                          message: 'Are you sure you want to close this ticket? This action cannot be undone.',
                          color: '#ef4444',
                          onConfirm: () => {
                            setConfirmModal(prev => ({...prev, show: false}));
                            updateTicketStatus(ticketDetails.id, 'closed');
                          }
                        });
                      }}
                      style={{
                        padding: '12px 20px',
                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                      }}
                    >
                      Close Ticket
                    </button>
                    <button
                      onClick={() => deleteTicket(ticketDetails.id)}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️ Delete Ticket
                    </button>
                  </div>
                )}

                {(ticketDetails.status === 'closed' || ticketDetails.deleted_at) && (
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', padding: '16px', backgroundColor: ticketDetails.deleted_at ? '#fef2f2' : '#f3f4f6', borderRadius: '8px', flexWrap: 'wrap' }}>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>
                      {ticketDetails.deleted_at ? '🗑️ This ticket has been archived' : '🔒 This ticket is closed'}
                    </p>
                    <button
                      onClick={() => reactivateTicket(ticketDetails.id)}
                      style={{
                        padding: '8px 20px', borderRadius: '8px', border: 'none',
                        backgroundColor: '#10b981', color: 'white', fontSize: '13px', fontWeight: '600',
                        cursor: 'pointer', boxShadow: '0 2px 6px rgba(16,185,129,0.3)'
                      }}
                    >
                      ♻️ Reactivate
                    </button>
                    {ticketDetails.status === 'closed' && !ticketDetails.deleted_at && (
                      <button
                        onClick={() => archiveTicket(ticketDetails.id)}
                        style={{
                          padding: '8px 20px', borderRadius: '8px', border: 'none',
                          backgroundColor: '#6b7280', color: 'white', fontSize: '13px', fontWeight: '600',
                          cursor: 'pointer', boxShadow: '0 2px 6px rgba(107,114,128,0.3)'
                        }}
                      >
                        📦 Archive
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archived Tickets Tab */}
      {activeTab === 'archived' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
            📦 Archived Tickets
          </h3>
          {archivedTickets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📦</div>
              <h4 style={{ margin: '0 0 8px 0', color: '#6b7280' }}>No archived tickets</h4>
              <p style={{ fontSize: '14px', margin: 0 }}>Closed and deleted tickets will appear here</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {archivedTickets.map((ticket: any) => (
                <div key={ticket.id} style={{
                  padding: '16px 20px', borderRadius: '10px',
                  border: ticket.deleted_at ? '1px solid #fecaca' : '1px solid #e5e7eb',
                  backgroundColor: ticket.deleted_at ? '#fef2f2' : '#f9fafb',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: '600', fontSize: '15px', color: '#1f2937' }}>
                        #{ticket.id} - {ticket.title}
                      </span>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                        backgroundColor: ticket.deleted_at ? '#fee2e2' : '#e0e7ff',
                        color: ticket.deleted_at ? '#dc2626' : '#4338ca'
                      }}>
                        {ticket.deleted_at ? 'DELETED' : 'CLOSED'}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {ticket.client_name || 'Unknown'} • {ticket.category} • {new Date(ticket.created_at).toLocaleDateString('en-NZ')}
                      {ticket.deleted_at && <span> • Deleted {new Date(ticket.deleted_at).toLocaleDateString('en-NZ')}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => { setSelectedTicket(ticket); fetchTicketDetails(ticket.id); }}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
                        backgroundColor: 'white', color: '#374151', fontSize: '13px', fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => reactivateTicket(ticket.id)}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', border: 'none',
                        backgroundColor: '#10b981', color: 'white', fontSize: '13px', fontWeight: '600',
                        cursor: 'pointer', boxShadow: '0 2px 6px rgba(16,185,129,0.3)'
                      }}
                    >
                      ♻️ Reactivate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, animation: 'fadeIn 0.2s ease-in'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '40px',
            textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideUp 0.3s ease-out', minWidth: '340px', maxWidth: '420px'
          }}>
            <div style={{
              width: '80px', height: '80px', backgroundColor: confirmModal.color,
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', animation: 'scaleIn 0.4s ease-out'
            }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: '600', color: '#1f2937' }}>
              {confirmModal.title}
            </h3>
            <p style={{ margin: '0 0 28px 0', fontSize: '15px', color: '#6b7280', lineHeight: '1.5' }}>
              {confirmModal.message}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmModal(prev => ({...prev, show: false}))}
                style={{
                  padding: '12px 28px', borderRadius: '10px', border: '1px solid #d1d5db',
                  backgroundColor: 'white', color: '#374151', fontSize: '15px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s'
                }}
                onMouseOver={e => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                onMouseOut={e => { e.currentTarget.style.backgroundColor = 'white'; }}
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                style={{
                  padding: '12px 28px', borderRadius: '10px', border: 'none',
                  backgroundColor: confirmModal.color, color: 'white', fontSize: '15px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s', boxShadow: `0 4px 12px ${confirmModal.color}40`
                }}
                onMouseOver={e => { e.currentTarget.style.opacity = '0.9'; }}
                onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.2s ease-in'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideUp 0.3s ease-out',
            minWidth: '300px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: successIcon === 'success' ? '#10b981' : successIcon === 'delete' ? '#ef4444' : successIcon === 'warning' ? '#f59e0b' : '#3b82f6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              animation: 'scaleIn 0.4s ease-out'
            }}>
              {successIcon === 'success' && (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              )}
              {successIcon === 'delete' && (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              )}
              {successIcon === 'warning' && (
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              )}
              {successIcon === 'info' && (
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              )}
            </div>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              {successIcon === 'success' ? 'Success!' : successIcon === 'delete' ? 'Deleted!' : successIcon === 'warning' ? 'Oops!' : 'Heads Up'}
            </h3>
            <p style={{
              margin: 0,
              fontSize: '16px',
              color: '#6b7280'
            }}>
              {successMessage}
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
        @keyframes urgentPulse {
          0%, 100% { border-color: #dc2626; box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          50% { border-color: #ef4444; box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
        }
      `}</style>

      {/* Client Help Button & Modal */}
      {isClientUser && <HelpGuide />}
    </div>
  )
};

export default Tickets;
